import React from 'react';
import styled, { css } from 'styled-components';
import { brand, brandHover, brandLight, info, infoText, danger, dangerLight, canvas, textSecondary, textPrimary, borderDefault } from '../../tokens/colors';
import { radiusMd } from '../../tokens/radii';
import { fontFamily, fontWeightBold } from '../../tokens/typography';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'info';
export type ButtonSize    = 'sm' | 'md' | 'lg';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  icon?: React.ReactNode;
}

const sizeStyles = {
  sm: css`padding: 5px 12px; font-size: 11px;`,
  md: css`padding: 7px 16px; font-size: 12px;`,
  lg: css`padding: 9px 20px; font-size: 13px;`,
};

const variantStyles = {
  primary: css`
    background: ${brand}; color: #fff; border: none;
    &:hover:not(:disabled) { background: ${brandHover}; }
  `,
  secondary: css`
    background: transparent; color: ${textSecondary}; border: 0.5px solid ${borderDefault};
    &:hover:not(:disabled) { background: ${canvas}; color: ${textPrimary}; }
  `,
  ghost: css`
    background: transparent; color: ${textSecondary}; border: none;
    &:hover:not(:disabled) { background: ${canvas}; color: ${textPrimary}; }
  `,
  danger: css`
    background: transparent; color: ${danger}; border: 0.5px solid #F09595;
    &:hover:not(:disabled) { background: ${dangerLight}; }
  `,
  info: css`
    background: ${info}; color: #fff; border: none;
    &:hover:not(:disabled) { background: ${infoText}; }
  `,
};

const StyledButton = styled.button<{ $variant: ButtonVariant; $size: ButtonSize }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border-radius: ${radiusMd};
  font-family: ${fontFamily};
  font-weight: ${fontWeightBold};
  cursor: pointer;
  transition: background 0.12s, color 0.12s;
  white-space: nowrap;
  ${({ $size }) => sizeStyles[$size]}
  ${({ $variant }) => variantStyles[$variant]}
  &:disabled { opacity: 0.45; cursor: not-allowed; }
`;

export const Button: React.FC<ButtonProps> = ({
  variant = 'secondary',
  size = 'md',
  loading = false,
  icon,
  children,
  disabled,
  ...rest
}) => (
  <StyledButton
    type="button"
    $variant={variant}
    $size={size}
    disabled={disabled || loading}
    {...rest}
  >
    {loading ? <Spinner /> : icon}
    {children}
  </StyledButton>
);

const Spinner = styled.span`
  display: inline-block;
  width: 12px; height: 12px;
  border: 2px solid rgba(255,255,255,0.4);
  border-top-color: currentColor;
  border-radius: 50%;
  animation: ds-spin 0.7s linear infinite;
  @keyframes ds-spin { to { transform: rotate(360deg); } }
`;
